import { ContentItemQuery, EventFormat } from '@lopo-tech/lopo-api'
import { AddToCalendarButton } from 'add-to-calendar-button-react'
import React, { FC, useMemo } from 'react'
import { CLASS_PREFIX } from 'src/constants'
import { POST_DISPLAY_MODE_DETAIL, PURPOSE_BASIC } from 'src/interfaces/posts'
import { textResources } from 'src/lang/de'
import { formatNeedsLink, formatNeedsPlace } from 'src/utils/event_format'
import BodyMarkdown from '../shared/ingredients/body_markdown/body_markdown'
import BodyText from '../shared/ingredients/body_text/body_text'
import { EventLocationContainer } from '../shared/ingredients/event_location/event_location_container'
import { EventStartEnd } from '../shared/ingredients/event_start_end/event_start_end'
import Image from '../shared/ingredients/image/image'
import ImageWithDate from '../shared/ingredients/image_with_date/image_with_date'
import IngredientLinkPreview from '../shared/ingredients/link_preview/link_preview'
import { MapFeaturedContainer } from '../shared/ingredients/map/map_featured_container'
import Title from '../shared/ingredients/title/title'

const _classPrefix = CLASS_PREFIX + 'post__content'

type Props = {
  post: NonNullable<ContentItemQuery['contentItem']>;
};

const displayMode = POST_DISPLAY_MODE_DETAIL
const purpose = PURPOSE_BASIC

const calendarLocation = (event: { address: { street?: string | null; zip?: string | null; city?: string | null }; format?: EventFormat | null; onlineMeetingUrl?: string | null }) => {
  const { address } = event

  if (formatNeedsPlace(event.format ?? undefined) && address.street && address.zip && address.city) {
    return `${address.street}, ${address.zip} ${address.city}`
  }

  // Only a format that has a link falls back to it. An in-person event with an
  // incomplete address has no link to offer, and used to hand out whatever was
  // left in the field.
  return formatNeedsLink(event.format ?? undefined)
    ? event.onlineMeetingUrl ?? ''
    : ''
}

export const PostContentBuilder: FC<Props> = ({ post }) => {
  const { id, content, attachments, title, oglink, markers, teaser } = post

  const { text: addToCalenderLabel } = textResources.addToCalender

  const event = post.__typename === 'Event' ? post : null
  const markdown = content.__typename === 'MarkdownContent' ? content : null

  const { startDate, endDate } = useMemo(() => {
    const today = new Date()
    const startDate = new Date(event?.startTime ?? today)
    const endDate = event?.endTime
      ? new Date(event.endTime)
      : new Date(startDate)

    if (!event?.endTime) {
      endDate.setHours(endDate.getHours() + 1)
    }

    return { startDate, endDate }
  }, [event?.startTime, event?.endTime])

  const [eventStartDate, eventStartTime] = getBerlinDateTime(startDate)
  const [eventEndDate, eventEndTime] = getBerlinDateTime(endDate)

  const blurType = !post.canSeeFullContent.value ? 'gradient' : undefined

  return (
    <>
      {event
        ? (
          <ImageWithDate
            postId={id}
            displayMode={displayMode}
            data={{ dateString: eventStartDate, images: attachments }}
            purpose={purpose}
          />
        )
        : (
          <Image
            postId={id}
            data={attachments}
            displayMode={displayMode}
            purpose={purpose}
          />
        )}
      {title && (
        <Title displayMode={displayMode} purpose={purpose} title={title} />
      )}
      {event && (
        <>
          <EventStartEnd
            data={{ start: startDate, end: endDate }}
            displayMode={displayMode}
            purpose={purpose}
          />
          <EventLocationContainer
            data={event.address}
            displayMode={displayMode}
            purpose={purpose}
            format={event.format}
            onlineMeetingUrl={event.onlineMeetingUrl}
          />
        </>
      )}
      {markdown
        ? (
          <BodyMarkdown
            blurred={blurType}
            displayMode={displayMode}
            text={markdown.text}
            shortcodes={markdown.shortcodes}
            purpose={purpose}
            teaserContent={teaser}
          />
        )
        : (
          <BodyText
            blurred={blurType}
            displayMode={displayMode}
            text={content.text ?? ''}
            purpose={purpose}
            teaserContent={teaser}
          />
        )
      }
      {oglink && (
        <IngredientLinkPreview
          data={{
            displayUrl: oglink.url,
            description: oglink.description,
            author: oglink.author,
            imageUrl: oglink.image?.previewUrl,
            title: oglink.title,
            url: oglink.url
          }}
          displayMode={displayMode}
          purpose={purpose}
        />
      )}
      {markers[0] && (
        <MapFeaturedContainer
          data={markers[0]}
          displayMode={displayMode}
          postId={id}
          purpose={purpose}
        />
      )}
      {event && event.title && (
        <div className={_classPrefix + '__tile__footer'}>
          <div className={_classPrefix + '__author-more-section'}>
            <AddToCalendarButton
              hideRichData
              label={addToCalenderLabel}
              name={event.title}
              // An online event has no place to go to; the link is what a
              // calendar entry can usefully carry instead.
              location={calendarLocation(event)}
              options={[
                'Apple',
                'Google',
                'iCal',
                'MicrosoftTeams',
                'Microsoft365',
                'Outlook.com',
                'Yahoo'
              ]}
              startDate={eventStartDate}
              endDate={eventEndDate}
              startTime={eventStartTime}
              endTime={eventEndTime}
              timeZone="Europe/Berlin"
            />
          </div>
        </div>
      )}
    </>
  )
}

const getBerlinDateTime = (date: number | Date | undefined) => {
  const berlinFormatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
  const [datePart, timePart] = berlinFormatter.format(date).split(' ')
  return [datePart, timePart]
}
